<script>
// Mixins
import Bootable from "@mixins/bootable";
import { factory as GroupableFactory } from "@mixins/groupable";

// Directives
import Touch from "@/core/directives/touch";

// Utilities
import { convertToUnit } from "@/core/util/helpers";
import mixins from "@/core/util/mixins";

const baseMixins = mixins(
  Bootable,
  GroupableFactory("tabsGroup", "tab-item", "tabs-items")
);

export default baseMixins.extend({
  name: "TabItem",

  directives: {
    Touch
  },

  props: {
    id: String,
    disabled: Boolean,
    reverseTransition: {
      type: [Boolean, String],
      default: undefined
    },
    transition: {
      type: [Boolean, String],
      default: undefined
    },
    value: {
      required: false
    }
  },

  data() {
    return {
      isActive: false,
      inTransition: false
    };
  },

  computed: {
    classes() {
      return this.groupClasses;
    },
    computedTransition() {
      if (!this.tabsGroup.internalReverse) {
        return typeof this.transition !== "undefined"
          ? this.transition || ""
          : this.tabsGroup.computedTransition;
      }

      return typeof this.reverseTransition !== "undefined"
        ? this.reverseTransition || ""
        : this.tabsGroup.computedTransition;
    }
  },

  methods: {
    genDefaultSlot() {
      return this.$slots.default;
    },
    genWindowItem() {
      const item = this.$createElement(
        "div",
        {
          staticClass: "tab-item",
          class: this.classes,
          directives: [
            {
              name: "show",
              value: this.isActive
            }
          ],
          on: this.$listeners
        },
        this.showLazyContent(this.genDefaultSlot())
      );

      if (!item.data.domProps) {
        item.data.domProps = {};
      }

      item.data.domProps.id = this.id || this.value;
      return item;
    },
    onAfterTransition() {
      if (!this.inTransition) {
        return;
      }

      // Finalize transition state.
      this.inTransition = false;
      if (this.tabsGroup.transitionCount > 0) {
        this.tabsGroup.transitionCount--;

        // Remove container height if we are out of transition.
        if (this.tabsGroup.transitionCount === 0) {
          this.tabsGroup.transitionHeight = undefined;
        }
      }
    },
    onBeforeTransition() {
      if (this.inTransition) {
        return;
      }

      // Initialize transition state here.
      this.inTransition = true;
      if (this.tabsGroup.transitionCount === 0) {
        // Set initial height for height transition.
        this.tabsGroup.transitionHeight = convertToUnit(
          this.tabsGroup.$el.clientHeight
        );
      }
      this.tabsGroup.transitionCount++;
    },
    onTransitionCancelled() {
      this.onAfterTransition(); // This should have the same path as normal transition end.
    },
    onEnter(el) {
      if (!this.inTransition) {
        return;
      }

      this.$nextTick(() => {
        // Do not set height if no transition or cancelled.
        if (!this.computedTransition || !this.inTransition) {
          return;
        }

        // Set transition target height.
        this.tabsGroup.transitionHeight = convertToUnit(el.clientHeight);
      });
    }
  },

  render(h) {
    return h(
      "transition",
      {
        props: {
          name: this.computedTransition
        },
        on: {
          // Handlers for enter windows.
          beforeEnter: this.onBeforeTransition,
          afterEnter: this.onAfterTransition,
          enterCancelled: this.onTransitionCancelled,

          // Handlers for leave windows.
          beforeLeave: this.onBeforeTransition,
          afterLeave: this.onAfterTransition,
          leaveCancelled: this.onTransitionCancelled,

          // Enter handler for height transition.
          enter: this.onEnter
        }
      },
      [this.genWindowItem()]
    );
  }
});
</script>
